import { FormEvent, useCallback, useEffect, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import logo from '../../assets/images/logos/logo_zignet_white.svg';
import { Loading } from '../../components/Loading';
import { PasswordRequirements } from '../../components/PasswordRequirements';
import { useAuth } from '../../hooks/auth';
import { emailValidation } from '../../utils/EmailValidation';
import { passwordPolicyRegex } from '../../utils/PasswordPolicy';
import { LoginStatusModal } from './components/LoginStatusModal';
import styles from './styles.module.scss';

export const Home = () => {
  const { user, signIn, forgotPassword, changePassword } = useAuth();
  const navigate = useNavigate();

  const [emailOK, setEmailOK] = useState<'notOk' | 'vazio' | 'ok'>('vazio');
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState('');

  const [passwordOK, setPasswordOK] = useState<'notOk' | 'vazio' | 'ok'>(
    'vazio'
  );
  const [passwordError, setPasswordError] = useState('');
  const [password, setPassword] = useState('');

  const [newPasswordOK, setNewPasswordOK] = useState<'notOk' | 'vazio' | 'ok'>(
    'vazio'
  );
  const [newPasswordError, setNewPasswordError] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [confirmNewPasswordOK, setConfirmNewPasswordOK] = useState<
    'notOk' | 'vazio' | 'ok'
  >('vazio');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [confirmationCodeOK, setConfirmationCodeOK] = useState<
    'notOk' | 'vazio' | 'ok'
  >('vazio');
  const [confirmationCodeError, setConfirmationCodeError] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  const [forgotEmailOK, setForgotEmailOK] = useState<
    'notOk' | 'vazio' | 'ok'
  >('vazio');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');

  const [visible, setVisible] = useState(true);

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<
    'SignIn' | 'ForgotEmail' | 'Forgot'
  >('SignIn');

  const [modal, setModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const handleInputEmail = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.trim();
      const emailValid = emailValidation({
        email: value,
        emptyEmail: 'Preenchimento obrigatório',
        incorrectEmail: 'Preenchimento incorreto',
      });
      setEmailOK(emailValid.status);
      setEmailError(emailValid.message);
      setEmail(value);
    },
    []
  );

  const handleInputForgotEmail = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.trim();
      const emailValid = emailValidation({
        email: value,
        emptyEmail: 'Preenchimento obrigatório',
        incorrectEmail: 'Preenchimento incorreto',
      });
      setForgotEmailOK(emailValid.status);
      setForgotEmailError(emailValid.message);
      setForgotEmail(value);
    },
    []
  );

  const handleInputPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.trim();
      const regex = /^(?=.*?[a-z])(?=.*?[0-9]).{6,}$/;
      if (value.length === 0) {
        setPasswordError('Preenchimento obrigatório');
        setPasswordOK('notOk');
      } else if (!value.match(regex)) {
        setPasswordError('Preenchimento incorreto');
        setPasswordOK('notOk');
      } else {
        setPasswordError('');
        setPasswordOK('ok');
      }

      setPassword(value);
    },
    []
  );

  const handleInputNewPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.trim();
      if (value.length === 0) {
        setNewPasswordError('Preenchimento obrigatório');
        setNewPasswordOK('notOk');
      } else if (!value.match(passwordPolicyRegex)) {
        setNewPasswordError(
          'A senha deve ter 8+ caracteres, maiúscula, minúscula, número e símbolo'
        );
        setNewPasswordOK('notOk');
      } else {
        setNewPasswordError('');
        setNewPasswordOK('ok');
      }

      setNewPassword(value);
    },
    []
  );

  const handleInputConfirmNewPassword = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.trim();
      if (value.length === 0) {
        setConfirmNewPasswordError('Preenchimento obrigatório');
        setConfirmNewPasswordOK('notOk');
      } else if (value !== newPassword) {
        setConfirmNewPasswordError('As senhas não são iguais');
        setConfirmNewPasswordOK('notOk');
      } else {
        setConfirmNewPasswordError('');
        setConfirmNewPasswordOK('ok');
      }
      setConfirmNewPassword(value);
    },
    [newPassword]
  );

  useEffect(() => {
    if (confirmNewPassword.length === 0) {
      return;
    }
    if (confirmNewPassword !== newPassword) {
      setConfirmNewPasswordError('As senhas não são iguais');
      setConfirmNewPasswordOK('notOk');
    } else {
      setConfirmNewPasswordError('');
      setConfirmNewPasswordOK('ok');
    }
  }, [newPassword, confirmNewPassword]);

  const handleInputCode = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      e.currentTarget.maxLength = 6;
      let { value } = e.currentTarget;
      value = value.replace(/\D/g, '');
      setConfirmationCode(value);
      // eslint-disable-next-line no-unused-expressions
      value.length < 6
        ? setConfirmationCodeOK('notOk')
        : setConfirmationCodeOK('ok');
      if (value.length === 0) {
        setConfirmationCodeError('Preenchimento obrigatório');
      } else if (value.length < 6) {
        setConfirmationCodeError('Preenchimento incorreto');
      } else {
        setConfirmationCodeError('');
      }
    },
    []
  );

  const resetForgotEmailField = useCallback(() => {
    setForgotEmail('');
    setForgotEmailOK('vazio');
    setForgotEmailError('');
  }, []);

  const resetPasswordResetFields = useCallback(() => {
    setConfirmationCode('');
    setConfirmationCodeOK('vazio');
    setConfirmationCodeError('');
    setNewPassword('');
    setNewPasswordOK('vazio');
    setNewPasswordError('');
    setConfirmNewPassword('');
    setConfirmNewPasswordOK('vazio');
    setConfirmNewPasswordError('');
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault();

        if (emailOK !== 'ok') {
          setEmailError('Preenchimento obrigatório');
          setEmailOK('notOk');
        }
        if (passwordOK !== 'ok') {
          setPasswordError('Preenchimento obrigatório');
          setPasswordOK('notOk');
        }
        if (emailOK !== 'ok' || passwordOK !== 'ok') {
          return;
        }

        setLoading(true);

        const result = await signIn({ email, password });

        if (result.status === 'success') {
          navigate('/admin');
        } else {
          const errorMessage =
            String(result.message).search(
              'Incorrect username or password.'
            ) > -1 || String(result.message).search('User not found') > -1
              ? 'Email / Senha incorreto'
              : 'Erro ao realizar login, tente novamente';
          setModalDescription('');
          setModal(true);
          setModalType('error');
          setModalMessage(errorMessage);
        }
      } catch (error: any) {
        setModalDescription('');
        setModal(true);
        setModalType('error');
        setModalMessage('Erro ao realizar login, tente novamente');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [email, emailOK, navigate, password, passwordOK, signIn]
  );

  const handleForgotPassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        if (forgotEmailOK !== 'ok') {
          setForgotEmailError('Preenchimento obrigatório');
          setForgotEmailOK('notOk');
          return;
        }

        setLoading(true);
        const result = await forgotPassword(forgotEmail);

        if (result.status === 'success') {
          resetPasswordResetFields();
          setFormState('Forgot');
          setModalMessage(result.data.message);
          setModalDescription(
            'Caso não receba o e-mail, entre em contato com o suporte para verificar se o seu e-mail está cadastrado no sistema. Por motivos de segurança, não informamos aqui se o e-mail está cadastrado ou não.'
          );
          setModalType('success');
          setModal(true);
        } else {
          const errorMessage =
            result.message ||
            'Erro ao enviar código de recuperação de senha, tente novamente!';
          setModalDescription('');
          setModal(true);
          setModalType('error');
          setModalMessage(errorMessage);
        }
      } catch (error) {
        setModalDescription('');
        setModal(true);
        setModalType('error');
        setModalMessage(
          'Erro ao solicitar processo de recuperação de senha, tente novamente'
        );
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [forgotEmail, forgotEmailOK, forgotPassword, resetPasswordResetFields]
  );

  const handleChangePassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        if (forgotEmailOK !== 'ok') {
          setForgotEmailError('Preenchimento obrigatório');
          setForgotEmailOK('notOk');
        }
        if (confirmationCodeOK !== 'ok') {
          setConfirmationCodeError('Preenchimento obrigatório');
          setConfirmationCodeOK('notOk');
        }
        if (newPasswordOK !== 'ok') {
          setNewPasswordError('Preenchimento obrigatório');
          setNewPasswordOK('notOk');
        }
        if (confirmNewPassword.length === 0) {
          setConfirmNewPasswordError('Preenchimento obrigatório');
          setConfirmNewPasswordOK('notOk');
        } else if (confirmNewPassword !== newPassword) {
          setConfirmNewPasswordError('As senhas não são iguais');
          setConfirmNewPasswordOK('notOk');
        }
        if (
          forgotEmailOK !== 'ok' ||
          confirmationCodeOK !== 'ok' ||
          newPasswordOK !== 'ok' ||
          confirmNewPasswordOK !== 'ok' ||
          newPassword !== confirmNewPassword
        ) {
          return;
        }

        setLoading(true);
        const result = await changePassword(
          forgotEmail,
          newPassword,
          confirmationCode
        );

        if (result.status === 'success') {
          setModalDescription('');
          setModalType('success');
          setModalMessage(result.data?.message || 'Senha redefinida com sucesso.');
          setModal(true);
          resetForgotEmailField();
          resetPasswordResetFields();
          setFormState('SignIn');
        } else {
          const errorMessage =
            result.message || 'Erro ao realizar troca de senha, tente novamente';
          setModalDescription('');
          setModal(true);
          setModalType('error');
          setModalMessage(errorMessage);
        }
      } catch (error) {
        setModalDescription('');
        setModal(true);
        setModalType('error');
        setModalMessage(
          'Erro ao solicitar processo de recuperação de senha, tente novamente'
        );
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      forgotEmailOK,
      confirmationCodeOK,
      newPasswordOK,
      confirmNewPasswordOK,
      changePassword,
      forgotEmail,
      newPassword,
      confirmationCode,
      confirmNewPassword,
      resetForgotEmailField,
      resetPasswordResetFields,
    ]
  );

  useEffect(() => {
    if (user.id) {
      navigate('/admin/dashboard');
    }
  }, [navigate, user]);

  return (
    <>
      {loading && <Loading />}
      {modal && (
        <LoginStatusModal
          setModal={setModal}
          text={modalMessage}
          description={modalDescription}
          type={modalType}
        />
      )}
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <div className={styles.zigLogo}>
            <img src={logo} alt="" />
          </div>
        </div>
        <div className={styles.formContainer}>
          {formState === 'SignIn' && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <h2 className={styles.formTitle}>Faça o seu login</h2>
              <div className={styles.formContent}>
                <div className={styles.inputsContainer}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={emailOK}>
                      <input
                        type="text"
                        name="email"
                        id="email"
                        placeholder=" "
                        onChange={handleInputEmail}
                        value={email}
                      />
                      <label htmlFor="email">Email</label>
                    </div>
                    <p className={styles.error}>{emailError}</p>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={passwordOK}>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="password"
                        id="password"
                        placeholder=" "
                        value={password}
                        onChange={handleInputPassword}
                      />
                      <label htmlFor="password">Senha</label>
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                    </div>
                    <p className={styles.error}>{passwordError}</p>
                  </div>
                </div>
                <div className={styles.formReminder}>
                  <div
                    className={styles.forgot}
                    onClick={() => {
                      resetForgotEmailField();
                      resetPasswordResetFields();
                      setFormState('ForgotEmail');
                    }}
                  >
                    Esqueceu sua senha?
                  </div>
                </div>
              </div>
              <div className={styles.buttonContainer}>
                <button type="submit">Entrar</button>
              </div>
            </form>
          )}

          {formState === 'ForgotEmail' && (
            <form className={styles.form} onSubmit={handleForgotPassword}>
              <h2 className={styles.formTitle}>Esqueci a Senha</h2>
              <p className={styles.formDescription}>
                Informe seu e-mail para receber o código de recuperação de
                senha.
              </p>
              <div className={styles.formContent}>
                <div className={styles.inputsContainer}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={forgotEmailOK}>
                      <input
                        type="text"
                        name="forgotEmail"
                        id="forgotEmail"
                        placeholder=" "
                        onChange={handleInputForgotEmail}
                        value={forgotEmail}
                      />
                      <label htmlFor="forgotEmail">Email</label>
                    </div>
                    <p className={styles.error}>{forgotEmailError}</p>
                  </div>
                </div>
              </div>
              <div className={styles.buttonContainer}>
                <button type="submit">Enviar código</button>
              </div>
              <div
                className={styles.link}
                onClick={() => {
                  resetForgotEmailField();
                  setFormState('SignIn');
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    resetForgotEmailField();
                    setFormState('SignIn');
                  }}
                >
                  Voltar
                </button>
              </div>
            </form>
          )}

          {formState === 'Forgot' && (
            <form className={styles.form} onSubmit={handleChangePassword}>
              <h2 className={styles.formTitle}>Esqueci a Senha</h2>
              <p
                className={`${styles.formDescription} ${styles.formDescriptionCompact}`}
              >
                Enviamos um código para o e-mail:{' '}
                <strong>{forgotEmail}</strong>
              </p>
              <div className={styles.formContent}>
                <div className={styles.inputsContainer}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={confirmationCodeOK}>
                      <input
                        type="text"
                        name="code"
                        id="code"
                        placeholder=" "
                        onChange={(e) => handleInputCode(e)}
                        value={confirmationCode}
                      />
                      <label htmlFor="code">Código de confirmação</label>
                    </div>
                    <p className={styles.error}>{confirmationCodeError}</p>
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={newPasswordOK}>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="newPassword"
                        id="newPassword"
                        placeholder=" "
                        value={newPassword}
                        onChange={handleInputNewPassword}
                      />
                      <label htmlFor="newPassword">Nova senha</label>
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                    </div>
                    <p className={styles.error}>{newPasswordError}</p>
                    <PasswordRequirements password={newPassword} />
                  </div>
                  <div className={styles.inputContainer}>
                    <div className={styles.input} role={confirmNewPasswordOK}>
                      <input
                        type={visible ? 'password' : 'text'}
                        name="confirmNewPassword"
                        id="confirmNewPassword"
                        placeholder=" "
                        value={confirmNewPassword}
                        onChange={handleInputConfirmNewPassword}
                      />
                      <label htmlFor="confirmNewPassword">
                        Confirmação Nova Senha
                      </label>
                      {visible ? (
                        <FiEyeOff
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      ) : (
                        <FiEye
                          onClick={() => {
                            setVisible(!visible);
                          }}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                      )}
                    </div>
                    <p className={styles.error}>{confirmNewPasswordError}</p>
                  </div>
                </div>
              </div>
              <div
                className={`${styles.buttonContainer} ${styles.forgotButtonContainer}`}
              >
                <button type="submit">Confirmar</button>
              </div>
              <div
                className={styles.link}
                onClick={() => {
                  resetForgotEmailField();
                  resetPasswordResetFields();
                  setFormState('SignIn');
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    resetForgotEmailField();
                    resetPasswordResetFields();
                    setFormState('SignIn');
                  }}
                >
                  Voltar
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};
